import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Check, X, ShieldCheck, AlertTriangle, FileText, ExternalLink, BadgeCheck, Scale } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'verified', label: 'Verified' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'all', label: 'All' },
];

const STATUS_BADGE = {
  pending: 'bg-amber-950/95 text-amber-300 border-amber-500/50',
  verified: 'bg-emerald-950/95 text-emerald-300 border-emerald-500/50',
  rejected: 'bg-red-950/95 text-red-300 border-red-500/50',
  suspended: 'bg-slate-900 text-slate-300 border-slate-500/40',
};

export const AdminProfessionalOnboardingTab = () => {
  const { token } = useAuth();
  const [statusFilter, setStatusFilter] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [actioning, setActioning] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/professional-onboarding/queue`, { params: { status: statusFilter }, headers: authHeaders });
      setItems(res.data.items || []);
    } catch {
      toast.error('Failed to load onboarding queue');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const handleAction = async (profile, action) => {
    let reason = '';
    if (action === 'reject' || action === 'suspend') {
      reason = window.prompt(`Reason for ${action}:`) || '';
      if (!reason.trim()) return;
    }
    setActioning(profile.id);
    try {
      await axios.post(`${API_URL}/api/admin/professional-onboarding/${profile.id}/${action}`,
        { reason }, { headers: authHeaders });
      toast.success(`${profile.user_email} ${action}d`);
      fetchQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setActioning(null);
    }
  };

  return (
    <div className="luxury-card p-6" data-testid="admin-professional-onboarding-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] font-semibold">Professional Onboarding (KYP)</h2>
          <p className="text-xs text-slate-500 mt-1">Know-Your-Professional verification queue — Bar Council / RERA / ICAI / SRA / RICS etc.</p>
        </div>
        <button type="button" onClick={fetchQueue} className="text-xs text-[#E07A3A] font-medium hover:underline">Refresh</button>
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2 mb-5 border-b border-slate-100 pb-3">
        {STATUS_TABS.map(t => (
          <button key={t.key} type="button" onClick={() => setStatusFilter(t.key)}
            className={`px-3 py-1.5 text-xs font-medium border transition-colors ${statusFilter === t.key ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#E07A3A]'}`}
            data-testid={`admin-kyp-filter-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#E07A3A] mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-sm text-slate-500">No {statusFilter} submissions.</div>
      ) : (
        <div className="space-y-3">
          {items.map(p => (
            <div key={p.id} className="border border-slate-200 bg-white" data-testid={`admin-kyp-row-${p.id}`}>
              <div className="flex items-center justify-between gap-3 p-4 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <BadgeCheck className="h-5 w-5 text-[#E07A3A] flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-[#1A1A1A] text-sm truncate">{p.user_name || p.user_email}</p>
                    <p className="text-xs text-slate-500 truncate">{p.professional_type} · {p.license_body} · #{p.license_number}{p.license_state ? ` · ${p.license_state}` : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2.5 py-0.5 text-[10px] font-semibold border uppercase tracking-wider ${STATUS_BADGE[p.status]}`}>{p.status}</span>
                  <button type="button" onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                    className="text-xs text-[#E07A3A] hover:underline font-medium" data-testid={`admin-kyp-expand-${p.id}`}>
                    {expanded === p.id ? 'Hide' : 'Details'}
                  </button>
                  {p.status === 'pending' && (
                    <>
                      <button type="button" onClick={() => handleAction(p, 'verify')} disabled={actioning === p.id}
                        className="bg-green-600 text-white hover:bg-green-700 px-3 py-1.5 text-xs font-medium flex items-center gap-1 disabled:opacity-50"
                        data-testid={`admin-kyp-verify-${p.id}`}>
                        <Check className="h-3 w-3" /> Verify
                      </button>
                      <button type="button" onClick={() => handleAction(p, 'reject')} disabled={actioning === p.id}
                        className="bg-red-600 text-white hover:bg-red-700 px-3 py-1.5 text-xs font-medium flex items-center gap-1 disabled:opacity-50"
                        data-testid={`admin-kyp-reject-${p.id}`}>
                        <X className="h-3 w-3" /> Reject
                      </button>
                    </>
                  )}
                  {p.status === 'verified' && (
                    <button type="button" onClick={() => handleAction(p, 'suspend')} disabled={actioning === p.id}
                      className="bg-slate-600 text-white hover:bg-slate-700 px-3 py-1.5 text-xs font-medium flex items-center gap-1 disabled:opacity-50"
                      data-testid={`admin-kyp-suspend-${p.id}`}>
                      <AlertTriangle className="h-3 w-3" /> Suspend
                    </button>
                  )}
                </div>
              </div>

              {expanded === p.id && (
                <div className="border-t border-slate-100 p-4 bg-[#F8FAFC] text-xs space-y-3" data-testid={`admin-kyp-details-${p.id}`}>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <DetailRow label="Country" value={p.country} />
                    <DetailRow label="Submitted" value={p.submitted_at?.slice(0, 19).replace('T', ' ')} />
                    <DetailRow label="NDA version" value={`v${p.nda_version || '—'}`} />
                    <DetailRow label="NDA signature" value={p.nda_signature_name} />
                    <DetailRow label="NDA IP" value={p.nda_signature_ip} />
                    <DetailRow label="Vault retention" value={`${p.vault_retention_days || 30} days`} />
                    <DetailRow label="Bio" value={p.bio || '—'} full />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {p.id_doc_url && <a href={p.id_doc_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#E07A3A] hover:underline"><FileText className="h-3 w-3" /> ID Doc <ExternalLink className="h-2.5 w-2.5" /></a>}
                    {p.license_doc_url && <a href={p.license_doc_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#E07A3A] hover:underline"><ShieldCheck className="h-3 w-3" /> Licence Doc <ExternalLink className="h-2.5 w-2.5" /></a>}
                  </div>
                  {p.fee_schedule?.length > 0 && (
                    <div>
                      <p className="font-semibold text-slate-700 mb-1 flex items-center gap-1"><Scale className="h-3 w-3" /> Fee schedule</p>
                      <ul className="space-y-1">
                        {p.fee_schedule.map((f, idx) => (
                          <li key={`fs-${idx}`} className="flex items-center justify-between bg-white border border-slate-200 px-2 py-1">
                            <span>{f.service}</span>
                            <span className="font-medium">{f.currency} {f.fee}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {p.rejection_reason && <p className="text-red-700"><b>Rejection reason:</b> {p.rejection_reason}</p>}
                  {p.admin_notes && <p className="text-slate-600"><b>Admin notes:</b> {p.admin_notes}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ label, value, full }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
    <p className="text-slate-700 break-words">{value || '—'}</p>
  </div>
);

export default AdminProfessionalOnboardingTab;
